/**
 * The exact binomial test of an observed number of successes against a hypothesised
 * probability p₀, with Clopper–Pearson confidence intervals.
 *
 * With X ~ Binomial(n, p₀) under H₀ and k successes observed:
 *
 * - less (p < p₀): P(X ≤ k);   greater (p > p₀): P(X ≥ k);
 * - two-sided: the total probability of every outcome no more likely than the one observed,
 *   P(X = j) ≤ P(X = k) (allowing 1e-7 relative slack for rounding) — the "minimum
 *   likelihood" rule of R's `binom.test` and `scipy.stats.binomtest`. It is not simply twice
 *   the smaller tail when p₀ ≠ ½, because the distribution is then skewed.
 *
 * Clopper–Pearson (1934) interval at level 1 − α: the lower bound is the α/2 beta quantile
 * Beta(k, n − k + 1), the upper the 1 − α/2 quantile of Beta(k + 1, n − k); a one-sided
 * alternative gets a one-sided interval ([0, upper] or [lower, 1]) at the full level, as in
 * R. The Wilson score interval is returned too, for comparison.
 *
 * Pinned against R 4.5.0 `binom.test` in `stats-tests-more.test.ts`.
 */

import { betaQuantile } from "@/lib/stats/beta-quantile";
import { binomialUpper } from "@/lib/stats/discrete-tails";
import { binomialCdf, binomialPmf } from "@/lib/stats/distributions";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";
import {
  cohensH,
  wilsonInterval,
} from "@/lib/tool-kit/stats-tests/proportions";

const RELATIVE_SLACK = 1 + 1e-7;

export interface BinomialTestResult {
  successes: number;
  trials: number;
  p0: number;
  pHat: number;
  alternative: Alternative;
  /** p-value for the chosen alternative. */
  p: number;
  pTwoSided: number;
  pLess: number;
  pGreater: number;
  level: number;
  /** Clopper–Pearson interval; one-sided alternatives give [0, upper] or [lower, 1]. */
  ci: [number, number];
  wilson: [number, number];
  /** Cohen's h for p̂ against p₀. */
  h: number;
  /** The most likely number of successes under H₀, n·p₀ rounded down. */
  expected: number;
  /** The outcomes counted in the two-sided p-value: [0, low] and [high, n]. */
  twoSidedRegion: { low: number; high: number } | null;
}

export type BinomialFailure =
  | "invalid-counts"
  | "invalid-probability"
  | "no-trials";

export type BinomialOutcome =
  | { ok: true; result: BinomialTestResult }
  | { ok: false; reason: BinomialFailure };

/** Clopper–Pearson interval for k of n; the level applies to each requested side. */
export function clopperPearsonInterval(
  k: number,
  n: number,
  level: number,
  alternative: Alternative
): [number, number] {
  const tail = alternative === "two-sided" ? (1 - level) / 2 : 1 - level;
  const lower = k === 0 ? 0 : betaQuantile(tail, k, n - k + 1);
  const upper = k === n ? 1 : betaQuantile(1 - tail, k + 1, n - k);
  if (alternative === "less") {
    return [0, upper];
  }
  if (alternative === "greater") {
    return [lower, 1];
  }
  return [lower, upper];
}

/**
 * The two tails of outcomes no more likely than k, as {low, high}: every j ≤ low and every
 * j ≥ high. The pmf rises to the mode and falls after it, so each boundary is found by
 * bisection instead of scanning all n + 1 outcomes.
 */
function minLikelihoodRegion(
  k: number,
  n: number,
  p0: number
): { low: number; high: number } | null {
  const observed = binomialPmf(k, n, p0) * RELATIVE_SLACK;
  const mode = Math.min(n, Math.floor((n + 1) * p0));
  // The lower tail: the largest j ≤ mode with pmf(j) ≤ observed.
  let low = -1;
  let a = 0;
  let b = mode;
  while (a <= b) {
    const mid = Math.floor((a + b) / 2);
    if (binomialPmf(mid, n, p0) <= observed) {
      low = mid;
      a = mid + 1;
    } else {
      b = mid - 1;
    }
  }
  // The upper tail: the smallest j ≥ mode with pmf(j) ≤ observed.
  let high = n + 1;
  a = mode;
  b = n;
  while (a <= b) {
    const mid = Math.floor((a + b) / 2);
    if (binomialPmf(mid, n, p0) <= observed) {
      high = mid;
      b = mid - 1;
    } else {
      a = mid + 1;
    }
  }
  if (low < 0 && high > n) {
    return null;
  }
  return { low, high };
}

export function binomialTest(
  successes: number,
  trials: number,
  p0: number,
  options: { alternative?: Alternative; level?: number } = {}
): BinomialOutcome {
  const alternative = options.alternative ?? "two-sided";
  const level = options.level ?? 0.95;
  if (
    !(Number.isInteger(successes) && Number.isInteger(trials)) ||
    successes < 0 ||
    successes > trials
  ) {
    return { ok: false, reason: "invalid-counts" };
  }
  if (trials < 1) {
    return { ok: false, reason: "no-trials" };
  }
  if (!(p0 > 0 && p0 < 1)) {
    return { ok: false, reason: "invalid-probability" };
  }
  const pLess = binomialCdf(successes, trials, p0);
  const pGreater = binomialUpper(successes, trials, p0);
  const region = minLikelihoodRegion(successes, trials, p0);
  const pTwoSided = region
    ? Math.min(
        1,
        (region.low >= 0 ? binomialCdf(region.low, trials, p0) : 0) +
          (region.high <= trials ? binomialUpper(region.high, trials, p0) : 0)
      )
    : 1;
  const p = {
    "two-sided": pTwoSided,
    less: pLess,
    greater: pGreater,
  }[alternative];
  const pHat = successes / trials;
  return {
    ok: true,
    result: {
      successes,
      trials,
      p0,
      pHat,
      alternative,
      p,
      pTwoSided,
      pLess,
      pGreater,
      level,
      ci: clopperPearsonInterval(successes, trials, level, alternative),
      wilson: wilsonInterval(successes, trials, level),
      h: cohensH(pHat, p0),
      expected: Math.floor(trials * p0),
      twoSidedRegion: region,
    },
  };
}

/** P(X = j) for each j in [from, to] under Binomial(n, p₀) — the working table. */
export function pmfRows(
  from: number,
  to: number,
  n: number,
  p0: number
): { k: number; p: number }[] {
  const rows: { k: number; p: number }[] = [];
  for (let k = from; k <= to; k++) {
    rows.push({ k, p: binomialPmf(k, n, p0) });
  }
  return rows;
}
